import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import { usePaginationFragment, usePreloadedQuery, useQueryLoader } from "react-relay";
import { Link } from "react-router-dom";
import { graphql } from "relay-runtime";

import { Button, Input, Paragraph, useDebouncedState } from "@skydio/rivet-ui";

import OrgNameUuidSelectFilter from "components/common/filters/OrgNameUuidFilter";
import { legacyToRivetPagination, useRelayPagination } from "components/common/Pagination";
import PaginatedTable from "components/common/table/PaginatedTable";
import { useQueryStringState } from "hooks/useQueryStringState";
import { formatOrgPrefixedPath } from "utils/common";

import AdminSearchEmptyState from "./AdminSearchEmptyState";

import styles from "../AdminSearchPage.module.scss";

import type { ColumnType } from "antd/lib/table";
import type { PreloadedQuery } from "react-relay";
import type { ConnectionNodeType } from "@skydio/relay-toolkit";
import type { Key } from "@skydio/rivet-ui";
import type {
  BatteriesList_batteries$data,
  BatteriesList_batteries$key,
} from "__generated__/BatteriesList_batteries.graphql";
import type { BatteriesListAdminQuery } from "__generated__/BatteriesListAdminQuery.graphql";
import type { BatteriesRefetchQuery } from "__generated__/BatteriesRefetchQuery.graphql";

export const rootQuery = graphql`
  query BatteriesListAdminQuery(
    $count: Int!
    $cursor: String
    $searchTerm: String
    $organizationUuid: String
    $maxVersion: String
  ) {
    ...BatteriesList_batteries
  }
`;

const batteriesFragment = graphql`
  fragment BatteriesList_batteries on Query @refetchable(queryName: "BatteriesRefetchQuery") {
    batteries(
      searchTerm: $searchTerm
      organizationUuid: $organizationUuid
      maxVersion: $maxVersion
      first: $count
      after: $cursor
    ) @connection(key: "BatteriesList_batteries") {
      totalCount
      edges {
        node {
          id
          displayName
          batterySerial
          versionString
          organizationId
          organization {
            id
            name
          }
        }
      }
    }
  }
`;

export interface TableProps {
  queryReference: PreloadedQuery<BatteriesListAdminQuery>;
}

const PAGE_SIZE = 10;
export type Battery = ConnectionNodeType<BatteriesList_batteries$data["batteries"]>;

export interface BatteriesPageContextType {
  setCount?: (count: number | undefined) => void;
}
export const BatteriesPageContext = createContext<BatteriesPageContextType | null>(null);
const BatteriesList: React.FC<{
  queryReference: PreloadedQuery<BatteriesListAdminQuery>;
}> = ({ queryReference }) => {
  const batteriesPageContext = useContext(BatteriesPageContext);
  const queryData = usePreloadedQuery<BatteriesListAdminQuery>(rootQuery, queryReference);

  const [searchTerm, setSearchTerm] = useQueryStringState("search", "");
  const [localSearchTerm, setLocalSearchTerm] = useQueryStringState("blsearch", "");

  useEffect(() => {
    if (localSearchTerm) {
      setSearchTerm("");
    }
  }, [localSearchTerm, setSearchTerm]);

  const [batteryNameSerialIdInput, handleBatteryNameSerialIdInput] = useDebouncedState<string>(
    localSearchTerm,
    setLocalSearchTerm
  );
  const [organizationUuid, setOrganizationUuid] = useState<Key | null>(null);
  const [maxVersion, setMaxVersion] = useState<string>("");

  const paginationQuery = usePaginationFragment<BatteriesRefetchQuery, BatteriesList_batteries$key>(
    batteriesFragment,
    queryData
  );

  const [paginationProps, currentPageEdges, resetPage] = useRelayPagination(
    paginationQuery,
    PAGE_SIZE,
    paginationQuery?.data?.batteries
  );

  const { refetch } = paginationQuery;
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    startTransition(() => {
      resetPage();

      let count = PAGE_SIZE;
      if (!localSearchTerm && !searchTerm && !organizationUuid && !maxVersion) count = 0;
      refetch({
        count: count,
        searchTerm: localSearchTerm ? localSearchTerm : searchTerm, // Prefer local searchTerm
        organizationUuid: organizationUuid as string,
        maxVersion: maxVersion as string,
      });
    });
  }, [refetch, resetPage, searchTerm, organizationUuid, maxVersion, localSearchTerm]);

  const currentPageNodes = currentPageEdges.map(edge => edge.node).filter(node => node !== null);

  const resetFilters = useCallback(() => {
    setLocalSearchTerm("");
    setSearchTerm("");
    setOrganizationUuid(null);
    setMaxVersion("");
    handleBatteryNameSerialIdInput("");
  }, [handleBatteryNameSerialIdInput, setLocalSearchTerm, setSearchTerm]);

  const getColumns = useCallback((): ColumnType<Battery>[] => {
    return [
      {
        title: "Name",
        render: (battery: Battery) => (
          <Paragraph>
            {battery.displayName &&
              (battery.organizationId ? (
                <Link
                  to={formatOrgPrefixedPath(
                    battery.organizationId,
                    `/settings/devices/batteries/${battery.batterySerial}`
                  )}
                >
                  {battery.displayName}
                  <div className="text-xs">{battery.batterySerial}</div>
                </Link>
              ) : (
                <>
                  {battery.displayName}
                  <div className="text-xs">{battery.batterySerial}</div>
                </>
              ))}
          </Paragraph>
        ),
      },
      {
        title: "Version",
        dataIndex: "versionString",
        render: (value: string | null) => <Paragraph>{value}</Paragraph>,
      },
      {
        title: "Organization",
        render: (battery: Battery) => (
          <Paragraph>
            {battery.organizationId && (
              <Link to={formatOrgPrefixedPath(battery.organizationId, "/settings/general")}>
                {battery.organization?.name}
              </Link>
            )}
          </Paragraph>
        ),
      },
    ];
  }, []);
  const columns = useMemo(() => getColumns(), [getColumns]);

  useEffect(() => {
    if (batteriesPageContext?.setCount) {
      batteriesPageContext?.setCount(
        paginationQuery?.data?.batteries?.edges.length ? paginationProps.totalCount : 0
      );
    }
  }, [
    batteriesPageContext,
    batteriesPageContext?.setCount,
    paginationProps.totalCount,
    paginationQuery?.data?.batteries?.edges.length,
  ]);

  return (
    <>
      <div className="flex w-full gap-4 pl-1">
        <Input
          placeholder="Battery Name/Serial"
          className={styles.search}
          value={batteryNameSerialIdInput}
          onValueChange={handleBatteryNameSerialIdInput}
          isClearable
        />
        <OrgNameUuidSelectFilter
          selectedKey={organizationUuid}
          onSingleSelectionChange={setOrganizationUuid}
        />
        <Input
          placeholder="Battery Max Version"
          className={styles.search}
          value={maxVersion}
          onValueChange={setMaxVersion}
          isClearable
        />
        <Button onClick={resetFilters} title="Reset filters">
          Reset
        </Button>
      </div>
      {!paginationQuery?.data?.batteries || paginationQuery?.data?.batteries.edges.length == 0 ? (
        <AdminSearchEmptyState
          hasResults={paginationProps.totalCount != 0}
          hasFilters={true}
          isLoading={isPending}
        />
      ) : (
        <PaginatedTable
          paginationProps={legacyToRivetPagination(paginationProps, resetPage)}
          columns={columns}
          dataSource={currentPageNodes}
          loading={isPending}
        />
      )}
    </>
  );
};

export const BatteriesPage = () => {
  const [queryReference, fetchBatteries] = useQueryLoader<BatteriesListAdminQuery>(rootQuery);
  useEffect(() => {
    fetchBatteries({ count: 0 });
  }, [fetchBatteries]);

  return queryReference && <BatteriesList queryReference={queryReference} />;
};

export default BatteriesList;
